import { type Control, settingValue, valueAttribute } from "./control.js";
import { controls } from "./controls.js";
import { defaultTier, type ServedTier } from "./dictionary.js";
import { defaultOptions, type ToolOptions } from "./options.js";

/**
 * The tool's state, in the address bar.
 *
 * So that a converter can be bookmarked or sent to somebody with the settings
 * and the text already in it: a teacher who always wants tone numbers keeps
 * one link, and a link to a converted sentence opens on that sentence.
 *
 * The parameters are named after the settings rather than packed into a blob,
 * so that `?notation=numbers`, or `?dictionary=full`, can be read — and
 * written — by somebody who has never opened the tool. Only settings that have
 * been moved off their default appear, which keeps an ordinary link short.
 *
 * Everything here is untrusted: a URL is the one input to these pages that did
 * not come from the person looking at it, so an unrecognised value falls back
 * to the default rather than being reported.
 */

/** The text to convert. */
const textParam = "q";

/**
 * Which dictionary to load.
 *
 * `dictionary` rather than `tier`, because this one is meant to be readable by
 * somebody who has not met the word "tier", and the button it moves says
 * "dictionary".
 */
const dictionaryParam = "dictionary";

/** What a URL asked for, with anything it did not ask for left out. */
export interface UrlState {
  /** Only the pinyin settings the URL named. */
  readonly options: Partial<ToolOptions>;
  readonly tier: ServedTier | undefined;
  readonly text?: string | undefined;
}

function isTier(value: string): value is ServedTier {
  return value === "standard" || value === "full";
}

/**
 * The value a control offers under a name a URL used for it.
 *
 * Matched without regard to case, unlike the radios, because this one gets
 * typed: somebody writing `?dictionary=Full` or `?locale=zh-tw` by hand means
 * the same thing as the canonical spelling, and gets back the control's own
 * value rather than what they wrote — which is what keeps `zh-TW` surviving a
 * round trip through a lowercased parameter.
 */
function offeredValue(
  control: Control,
  given: string,
): string | boolean | undefined {
  const wanted = given.trim().toLowerCase();

  return control.values.find(
    (offered) => valueAttribute(offered.value).toLowerCase() === wanted,
  )?.value;
}

/**
 * Whatever a URL has to say about one tool's settings.
 *
 * The parameters are the control fields, so a tool's settings appear in the
 * address bar under the names the tool itself uses for them and no list of
 * parameter names has to be kept in step with a list of controls.
 */
function readControlSettings<Settings extends object>(
  parameters: URLSearchParams,
  controlList: readonly Control[],
): Partial<Settings> {
  let settings: Partial<Settings> = {};

  for (const control of controlList) {
    const given = parameters.get(control.field);
    if (given === null) {
      continue;
    }

    const value = offeredValue(control, given);
    if (value !== undefined) {
      // The field is one of the settings' own by construction, which is
      // exactly what a computed key cannot say for itself.
      settings = { ...settings, [control.field]: value };
    }
  }

  return settings;
}

/**
 * Read whatever a URL has to say about the tool's state.
 */
export function readUrlState(search: string): UrlState {
  const parameters = new URLSearchParams(search);

  const dictionary = parameters.get(dictionaryParam)?.trim().toLowerCase();
  const text = parameters.get(textParam);

  return {
    options: readControlSettings<ToolOptions>(parameters, controls),
    tier:
      dictionary !== undefined && isTier(dictionary) ? dictionary : undefined,
    ...(text !== null && { text }),
  };
}

/**
 * What a tool wants written into the address bar.
 *
 * Every field but the text is optional, because no tool has all of them:
 * converting pinyin to bopomofo has nothing to configure and nothing to
 * download, and the script converter asks nothing at all, so those pages carry
 * only their text.
 */
export interface WritableState {
  readonly text: string;
  readonly options?: ToolOptions | undefined;
  readonly tier?: ServedTier | undefined;
}

/**
 * Put a tool's settings into a query string, leaving out the untouched ones.
 *
 * Nothing at all for a tool that has no settings, which is every tool but the
 * pinyin converter.
 */
function writeControlSettings(
  parameters: URLSearchParams,
  controlList: readonly Control[],
  settings: object | undefined,
  defaults: object,
): void {
  if (settings === undefined) {
    return;
  }

  for (const control of controlList) {
    const value = settingValue(settings, control.field);

    if (
      value !== undefined &&
      value !== settingValue(defaults, control.field)
    ) {
      parameters.set(control.field, valueAttribute(value));
    }
  }
}

/**
 * The query string for a state, holding only what differs from the defaults.
 *
 * An untouched tool on an empty input writes nothing at all, so arriving at
 * the page and leaving it alone does not put a query string in the address
 * bar for no reason.
 */
export function urlSearch(state: WritableState): string {
  const parameters = new URLSearchParams();

  if (state.text !== "") {
    parameters.set(textParam, state.text);
  }

  writeControlSettings(parameters, controls, state.options, defaultOptions);

  if (state.tier !== undefined && state.tier !== defaultTier) {
    parameters.set(dictionaryParam, state.tier);
  }

  return parameters.toString();
}

/**
 * The address a state would be found at, given where the page is now.
 */
export function urlFor(href: string, state: WritableState): string {
  const url = new URL(href);
  url.search = urlSearch(state);

  return url.href;
}

/**
 * Put a state in the address bar, without adding to the history.
 *
 * `replaceState` rather than `pushState`: the address bar tracking what the
 * page is showing is what makes a bookmark work, but a history entry per
 * keystroke would make the back button useless.
 */
export function rememberUrl(state: WritableState): void {
  history.replaceState(null, "", urlFor(location.href, state));
}
